// =============================================================================
// features/sales/campaigns/operate/components/OperationFooter.tsx — Rodapé
// fixo: ações de contato + formulário de resultado (doc 26 §6.8).
//
// "Próximo cliente" só habilita quando `resolved` (resultado registrado OU
// devolução justificada, ambos liberando a reserva no servidor) ou quando
// `leaseLost` (o servidor já confirmou que este item não é mais nosso — não
// faz sentido continuar bloqueando a tela por um item que já não é seu,
// briefing do slot). Enquanto nenhum dos dois é verdadeiro, o botão fica
// visível e DESABILITADO com um texto explicando o porquê (briefing #3 —
// nunca só a cor/estado do botão).
//
// A mensagem de "canAdvance" (F35-S18) reflete o `status` que o SERVIDOR
// devolveu em `resolvedMember` — nunca o `outcomeCode` local — porque só o
// servidor sabe se o resolve realmente encerrou o membro (`completed`),
// agendou um retorno (`waiting_callback`) ou (caminho de "devolver sem
// registrar", sempre `release`) só devolveu para a fila (`available`). Três
// desfechos distintos, três textos distintos — nunca um genérico.
//
// Click-to-call: FORA de escopo deste slot (F36-S07 pluga `CallControl`
// aqui, doc 26 §6.8 "Fora de escopo"). O botão "Ligar" existe mas é inerte —
// texto explica a limitação em vez de fingir que funciona.
// =============================================================================

import type {
  CampaignQueueLeadContext,
  CampaignQueueMember,
  SalesOutcomeCode,
} from '@elemento/shared-schemas';
import * as React from 'react';

import { Button } from '../../../../../components/ui/Button';
import { useContactPreferences } from '../../../../../hooks/sales/useContactPreferences';
import { RegisterContactDialog } from '../../../contacts/RegisterContactDialog';
import { resolveAdvanceMessage } from '../footerMessage';

import { ReturnMemberDialog } from './ReturnMemberDialog';

export interface OperationFooterProps {
  campaignId: string;
  member: CampaignQueueMember;
  lead: CampaignQueueLeadContext;
  resolved: boolean;
  /** O `member` devolvido pela chamada (resolve OU release) que produziu `resolved`. */
  resolvedMember: CampaignQueueMember | null;
  leaseLost: boolean;
  isReleasing: boolean;
  isResolving: boolean;
  onReleaseAndAdvance: (
    reason: string | undefined,
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
  onResolveAndAdvance: (
    outcomeCode: SalesOutcomeCode,
    nextActionAt: string | undefined,
  ) => Promise<void>;
  onNext: () => void;
}

type DialogState = 'register' | 'return' | null;

export function OperationFooter({
  lead,
  resolved,
  resolvedMember,
  leaseLost,
  isReleasing,
  isResolving,
  onReleaseAndAdvance,
  onResolveAndAdvance,
  onNext,
}: OperationFooterProps): React.JSX.Element {
  const [dialog, setDialog] = React.useState<DialogState>(null);

  const { blockedChannels, isFullyBlocked } = useContactPreferences(lead.id);

  const canAdvance = resolved || leaseLost;
  const isBusy = isReleasing || isResolving;

  return (
    <div
      className="sticky bottom-0 left-0 right-0 flex flex-col gap-4 border-t px-4 py-4 sm:px-6"
      style={{
        background: 'var(--bg-elev-1)',
        borderColor: 'var(--border)',
        boxShadow: '0 -8px 24px rgba(0,0,0,0.08)',
      }}
    >
      {/* Ações de contato — inertes até F36-S07 (click-to-call) */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          type="button"
          variant="outline"
          disabled
          title="Discagem automática chega em F36 — use seu telefone com o número acima."
        >
          Ligar (em breve)
        </Button>
        <p className="font-sans text-xs text-ink-4">
          Discagem automática ainda não está disponível — copie o número ao lado e ligue pelo seu
          telefone.
        </p>
      </div>

      {canAdvance ? (
        <div
          className="flex items-center justify-between gap-4 flex-wrap rounded-sm px-4 py-3"
          style={{ background: 'var(--success-bg)' }}
        >
          <p className="font-sans text-sm font-semibold text-success">
            {resolveAdvanceMessage({ leaseLost, resolved, resolvedMember })}
          </p>
          <Button type="button" variant="primary" onClick={onNext}>
            Próximo cliente
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {isFullyBlocked && (
            <p className="font-sans text-xs text-danger">
              Contato bloqueado em todos os canais — você só pode devolver este item, não registrar
              um novo contato.
            </p>
          )}

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                type="button"
                variant="primary"
                onClick={() => setDialog('register')}
                disabled={isFullyBlocked || isBusy}
              >
                Registrar resultado
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialog('return')}
                disabled={isBusy}
              >
                Devolver sem registrar
              </Button>
            </div>

            <div className="flex flex-col items-end gap-1">
              <Button type="button" variant="primary" disabled aria-describedby="next-client-hint">
                Próximo cliente
              </Button>
              <p
                id="next-client-hint"
                className="font-sans text-xs text-ink-3 max-w-[240px] text-right"
              >
                Registre o resultado ou devolva o cliente para liberar o próximo.
              </p>
            </div>
          </div>
        </div>
      )}

      {dialog === 'register' && (
        <RegisterContactDialog
          leadId={lead.id}
          leadName={lead.name}
          blockedChannels={blockedChannels}
          origin="campaign_queue"
          onClose={() => setDialog(null)}
          onRegistered={() => setDialog(null)}
          onResolveQueueMember={(outcomeCode, nextActionAt) =>
            onResolveAndAdvance(outcomeCode, nextActionAt)
          }
        />
      )}

      {dialog === 'return' && (
        <ReturnMemberDialog
          leadName={lead.name}
          onClose={() => setDialog(null)}
          onConfirm={(reason) => onReleaseAndAdvance(reason)}
        />
      )}
    </div>
  );
}
